import type { MouseEvent } from "react";

import { ChevronLeftIcon, ChevronRightIcon } from "@/components/appIcons";
import { Link, type LinkTarget } from "@/components/link";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type PaginationItem = number | "ellipsis-start" | "ellipsis-end";

function getPaginationItems(currentPage: number, totalPages: number): PaginationItem[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set([1, totalPages, currentPage - 1, currentPage, currentPage + 1]);
  const visiblePages = [...pages]
    .filter((page) => page > 0 && page <= totalPages)
    .sort((a, b) => a - b);
  const items: PaginationItem[] = [];

  visiblePages.forEach((page, index) => {
    const previousPage = visiblePages[index - 1];
    if (previousPage && page - previousPage > 1) {
      items.push(previousPage === 1 ? "ellipsis-start" : "ellipsis-end");
    }
    items.push(page);
  });

  return items;
}

interface WorkReleasePaginationProps {
  currentPage: number;
  getPageLinkTarget: (page: number) => LinkTarget;
  isFetching: boolean;
  nextLabel: string;
  onPageNavigate: (page: number) => void;
  pageLabel: string;
  previousLabel: string;
  totalPages: number;
}

export function WorkReleasePagination({
  currentPage,
  getPageLinkTarget,
  isFetching,
  nextLabel,
  onPageNavigate,
  pageLabel,
  previousLabel,
  totalPages,
}: WorkReleasePaginationProps) {
  if (totalPages <= 1) return null;

  function handleLinkClick(event: MouseEvent<HTMLAnchorElement>, page: number) {
    if (isFetching) {
      event.preventDefault();
      return;
    }
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      return;
    onPageNavigate(page);
  }

  const disabledLinkClass = "pointer-events-none opacity-50";

  return (
    <nav aria-label={pageLabel} className="flex flex-wrap items-center justify-center gap-2 pt-8">
      {currentPage > 1 ? (
        <Link
          {...getPageLinkTarget(currentPage - 1)}
          resetScroll={false}
          className={cn(
            buttonVariants({ variant: "outline", size: "sm" }),
            isFetching && disabledLinkClass,
          )}
          aria-disabled={isFetching || undefined}
          onClick={(event) => handleLinkClick(event, currentPage - 1)}
        >
          <ChevronLeftIcon />
          <span className="hidden sm:inline">{previousLabel}</span>
        </Link>
      ) : (
        <span
          className={cn(buttonVariants({ variant: "outline", size: "sm" }), disabledLinkClass)}
          aria-disabled="true"
        >
          <ChevronLeftIcon />
          <span className="hidden sm:inline">{previousLabel}</span>
        </span>
      )}

      <div className="flex items-center gap-1" aria-label={pageLabel}>
        {getPaginationItems(currentPage, totalPages).map((item) =>
          typeof item === "number" ? (
            <Link
              key={item}
              {...getPageLinkTarget(item)}
              resetScroll={false}
              className={cn(
                buttonVariants({
                  variant: item === currentPage ? "default" : "ghost",
                  size: "icon-sm",
                }),
                isFetching && disabledLinkClass,
              )}
              aria-current={item === currentPage ? "page" : undefined}
              aria-label={`${item}`}
              aria-disabled={isFetching || undefined}
              onClick={(event) => handleLinkClick(event, item)}
            >
              {item}
            </Link>
          ) : (
            <span key={item} aria-hidden="true" className="px-1 text-muted-foreground">
              …
            </span>
          ),
        )}
      </div>

      {currentPage < totalPages ? (
        <Link
          {...getPageLinkTarget(currentPage + 1)}
          resetScroll={false}
          className={cn(
            buttonVariants({ variant: "outline", size: "sm" }),
            isFetching && disabledLinkClass,
          )}
          aria-disabled={isFetching || undefined}
          onClick={(event) => handleLinkClick(event, currentPage + 1)}
        >
          <span className="hidden sm:inline">{nextLabel}</span>
          <ChevronRightIcon />
        </Link>
      ) : (
        <span
          className={cn(buttonVariants({ variant: "outline", size: "sm" }), disabledLinkClass)}
          aria-disabled="true"
        >
          <span className="hidden sm:inline">{nextLabel}</span>
          <ChevronRightIcon />
        </span>
      )}
    </nav>
  );
}
